if (!customElements.get('bundle-collection-grid')) {
  class BundleCollectionGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-bundle-grid]');
      this.status = this.querySelector('[data-bundle-status]');
      this.empty = this.querySelector('[data-bundle-empty]');
      this.loadButton = this.querySelector('[data-bundle-load-more]');
      this.activeCategory = 'all';
      this.activeBundleType = this.dataset.defaultBundleType || 'goal';
      this.loading = false;
      this.abortController = new AbortController();
      this.addEventListener('click', this.handleClick.bind(this), { signal: this.abortController.signal });
      this.syncTypeButtons();
      this.updateView();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    get cards() {
      return Array.from(this.querySelectorAll('[data-bundle-card]'));
    }

    handleClick(event) {
      const typeButton = event.target.closest('[data-bundle-type-filter]');
      if (typeButton && this.contains(typeButton)) {
        this.selectBundleType(typeButton.dataset.bundleTypeFilter);
        return;
      }

      const filterButton = event.target.closest('[data-bundle-filter]');
      if (filterButton && this.contains(filterButton)) {
        this.selectCategory(filterButton.dataset.bundleFilter);
        return;
      }

      const loadButton = event.target.closest('[data-bundle-load-more]');
      if (loadButton && this.contains(loadButton)) this.loadNextPage();
    }

    syncTypeButtons() {
      this.querySelectorAll('[data-bundle-type-filter]').forEach((button) => {
        const selected = button.dataset.bundleTypeFilter === this.activeBundleType;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }

    async selectBundleType(type) {
      if (this.loading || !type || type === this.activeBundleType) return;

      this.activeBundleType = type;
      this.syncTypeButtons();

      // Fetch the remaining pages before filtering so a tab is never limited
      // to whichever bundles happened to be on the first pagination page.
      if (this.dataset.nextUrl) await this.loadAllPages();
      this.updateView();
    }

    async selectCategory(category) {
      if (this.loading || !category) return;
      this.activeCategory = category;
      this.querySelectorAll('[data-bundle-filter]').forEach((button) => {
        const selected = button.dataset.bundleFilter === category;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      if (category !== 'all' && this.dataset.nextUrl) await this.loadAllPages();
      this.updateView();
    }

    async loadAllPages() {
      this.setLoading(true);
      try {
        while (this.dataset.nextUrl) await this.fetchNextPage();
      } catch (error) {
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }

    async loadNextPage() {
      if (this.loading || !this.dataset.nextUrl) return;
      this.setLoading(true);
      try {
        await this.fetchNextPage();
        this.updateView();
      } catch (error) {
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }

    async fetchNextPage() {
      const nextUrl = new URL(this.dataset.nextUrl, window.location.origin);
      nextUrl.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(nextUrl.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Bundle collection request failed: ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const incomingSection = documentFragment.querySelector('bundle-collection-grid');
      if (!incomingSection) throw new Error('Bundle collection section was not found.');

      incomingSection.querySelectorAll('[data-bundle-card]').forEach((card) => this.grid.append(card));
      this.dataset.nextUrl = incomingSection.dataset.nextUrl || '';
      if (!this.dataset.nextUrl && this.loadButton) {
        this.loadButton.closest('.plbc-load-more')?.remove();
        this.loadButton = null;
      }
    }

    updateView() {
      let visible = 0;

      this.cards.forEach((card) => {
        const categories = (card.dataset.bundleCategories || '').split('|').filter(Boolean);
        const bundleType = card.dataset.bundleType || 'brand';

        const matchesType = bundleType === this.activeBundleType;
        const matchesCategory = this.activeCategory === 'all' || categories.includes(this.activeCategory);
        const show = matchesType && matchesCategory;

        card.hidden = !show;
        if (show) visible += 1;
      });

      if (this.empty) this.empty.hidden = visible !== 0;
      if (this.status) this.status.textContent = `${visible} ${visible === 1 ? 'bundle' : 'bundles'}`;
    }

    setLoading(loading) {
      this.loading = loading;
      this.setAttribute('aria-busy', String(loading));
      this.querySelectorAll('[data-bundle-filter], [data-bundle-type-filter]').forEach((button) => {
        button.disabled = loading;
      });
      if (this.loadButton) {
        this.loadButton.disabled = loading;
        this.loadButton.textContent = loading ? this.dataset.loadingLabel : this.dataset.loadLabel;
      }
    }

    showError() {
      if (this.status) this.status.textContent = this.dataset.errorLabel;
    }
  }

  customElements.define('bundle-collection-grid', BundleCollectionGrid);
}
